import axios from 'axios';

const API_BASE = process.env.REACT_APP_WC_API_BASE || '';
const CONSUMER_KEY = process.env.REACT_APP_WC_CONSUMER_KEY || '';
const CONSUMER_SECRET = process.env.REACT_APP_WC_CONSUMER_SECRET || '';

export interface Product {
  id: number;
  name: string;
  category: string;
  tags: string[];
  price: number;
  image: string;
  quantity?: number;
  originalPrice?: number;
  discountPercent?: number;
}

export interface PaginatedProducts {
  products: Product[];
  hasNextPage: boolean;
}

export interface Order {
  id: number;
  status: string;
  date_created: string;
  billing: {
    phone: string;
    [key: string]: any;
  };
  line_items: any[];
  [key: string]: any;
}

export interface PaginatedOrders {
  orders: Order[];
  hasNextPage: boolean;
}


// Basic Auth Header
const getBasicAuthHeader = () => {
  const token = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
  return { Authorization: `Basic ${token}` };
};

// Fetch WooCommerce products, optionally filtered by category ID
export const fetchWooProductsPaginated = async (
  page: number,
  successCallBack: (data: PaginatedProducts) => void,
  errorCallBack: (error: any) => void,
  categoryId: number = 0
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: '6',
    orderby: 'date',
    order: 'desc',
    stock_status: 'instock',
    ...(categoryId !== 0 ? { category: categoryId.toString() } : {}),
  }).toString();

  const url = `${API_BASE}/products?${queryParams}`;

  try {
    const { data } = await axios.get(url, {
      headers: {
        ...getBasicAuthHeader(),
        Accept: 'application/json',
        'X-Client-Component': 'home',
      },
    });

    const products: Product[] = data.map((p: any) => ({
      id: p.id,
      name: p.name,
      category: p.categories?.[0]?.name || 'All',
      tags: p.tags?.map((t: any) => t.name.toLowerCase()) || [],
      price: parseFloat(p.price),
      originalPrice: p.regular_price ? parseFloat(p.regular_price) : undefined,
      discountPercent:
        p.regular_price && p.sale_price
          ? Math.round(
              ((parseFloat(p.regular_price) - parseFloat(p.sale_price)) /
                parseFloat(p.regular_price)) *
                100
            )
          : 0,
      image: p.images?.[0]?.src || '',
    }));

    successCallBack({
      products,
      hasNextPage: data.length === 6,
    });
  } catch (err) {
    errorCallBack(err);
  }
};

// Create WooCommerce order with dynamic customer info and line items
export const createWooOrder = async (
  firstName: string,
  lastName: string,
  phone: string,
  lineItems: { product_id: number; quantity: number }[]
) => {
  const url = `${API_BASE}/orders`;

  const headers = {
    ...getBasicAuthHeader(),
    'Content-Type': 'application/json',
    'X-Client-Component': 'cart',
  };

  const orderPayload = {
    payment_method: 'cod',
    payment_method_title: 'Cash on Delivery',
    set_paid: true,
    status: 'processing', 
    billing: {
      first_name: firstName,
      last_name: lastName,
      address_1: 'Default Street',
      address_2: '',
      city: 'Bengaluru',
      state: 'KA',
      postcode: '560001',
      country: 'IN',
      email: `${firstName}${phone}@email.com`,
      phone: phone,
    },
    shipping: {
      first_name: firstName,
      last_name: lastName,
      address_1: 'Default Street',
      address_2: '',
      city: 'Bengaluru',
      state: 'KA',
      postcode: '560001',
      country: 'IN',
    },
    line_items: lineItems,
  };

  try {
    const { data } = await axios.post(url, orderPayload, { headers });
    return { success: true, data };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};
//Fetching past ordered products
export const fetchPastOrderedProductsPaginated = async (
  phone: string,
  page: number,
  perPage: number = 8,
  existingProductIds: Set<number> = new Set()
) => {
  try {
    const ordersResponse = await axios.get(`${API_BASE}/orders`, {
      params: { per_page: perPage, page },
      headers: { ...getBasicAuthHeader(), 'X-Client-Component': 'buy-again' }
    });

    const orders = ordersResponse.data.filter(
      (order: any) => order?.billing?.phone === phone
    );

    const productMap: { [productId: number]: any } = {};

    const productPromises: Promise<void>[] = [];

    for (const order of orders) {
      for (const item of order.line_items) {
        const productId = item.product_id;
        if (!existingProductIds.has(productId) && !productMap[productId]) {
          const promise = axios
            .get(`${API_BASE}/products/${productId}`, { headers:  { ...getBasicAuthHeader(), 'X-Client-Component': 'buy-again' } })
            .then(res => {
              const productData = res.data;
              productMap[productId] = {
                id: productId,
                name: productData.name,
                price: parseFloat(item.total) / item.quantity,
                quantity: item.quantity,
                image: productData.images?.[0]?.src || '',
                originalPrice: productData.regular_price
                  ? parseFloat(productData.regular_price)
                  : undefined,
                discountPercent:
                  productData.regular_price && productData.price
                    ? Math.round(
                        ((parseFloat(productData.regular_price) -
                          parseFloat(productData.price)) /
                          parseFloat(productData.regular_price)) *
                          100
                      )
                    : 0,
              };
            })
            .catch(err => console.error(`Failed to fetch product ${productId}:`, err));

          productPromises.push(promise);
        }
      }
    }

    await Promise.all(productPromises);
    Object.keys(productMap).forEach(id => existingProductIds.add(Number(id)));

    return {
      products: Object.values(productMap),
      hasNextPage: ordersResponse.data.length === perPage,
      existingProductIds,
    };
  } catch (error) {
    console.error('Error fetching past orders:', error);
    return { products: [], hasNextPage: false, existingProductIds };
  }
};

// Fetch WooCommerce orders by phone, paginated
export const fetchWooOrdersByPhonePaginated = async (
  phone: string,
  page: number,
  successCallBack: (data: PaginatedOrders) => void,
  errorCallBack: (error: any) => void,
  perPage: number = 8,
  status?: string
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
    orderby: 'date',
    order: 'desc',
    ...(status ? { status } : {}),
  }).toString();

  const url = `${API_BASE}/orders?${queryParams}`;

  try {
    const { data } = await axios.get(url, {
      headers: {
        ...getBasicAuthHeader(),
        Accept: 'application/json',
        'X-Client-Component': 'your-orders',
      },
    });
//filter by phone
    const filteredOrders: Order[] = data.filter(
      (order: any) => order.billing?.phone === phone
    );

    successCallBack({
      orders: filteredOrders,
      hasNextPage: data.length === perPage,
    });
  } catch (err) {
    errorCallBack(err);
  }
};
// Cancel WooCommerce order
export const cancelWooOrder = async (orderId: number) => {
  const url = `${API_BASE}/orders/${orderId}`;

  const headers = {
    ...getBasicAuthHeader(),
    'Content-Type': 'application/json',
    'X-Client-Component': 'your-orders',
  };

  try {
    const { data } = await axios.put(
      url,
      { status: 'cancelled' },
      { headers }
    );
    return { success: true, data };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};
